import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, FileText, ArrowLeft, Lock, Eye, EyeOff,
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_LABELS = { kpir: 'KPiR', ryczalt: 'Ryczałt', pelna: 'Pełna' };
const STATUS_LABELS_PL = { new: 'Nowy', contacted: 'W kontakcie', converted: 'Zamknięty', rejected: 'Odrzucony' };
const BAR_COLORS = { kpir: '#1B3A7B', ryczalt: '#E8941A', pelna: '#16a34a' };
const STATUS_COLORS = { new: '#3b82f6', contacted: '#eab308', converted: '#22c55e', rejected: '#ef4444' };

function AdminLoginGate() {
  const { language } = useLanguage();
  const { login } = useAdmin();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try { await login(password); } catch { setError(language === 'pl' ? 'Nieprawidłowe hasło' : 'Invalid password'); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="admin-login-gate">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-[#1B3A7B] text-white flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6" /></div>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A7B]">{language === 'pl' ? 'Panel administratora' : 'Admin Panel'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input type={showPw ? 'text' : 'password'} placeholder={language === 'pl' ? 'Hasło administratora' : 'Admin password'} value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} className={`h-12 pr-10 ${error ? 'border-red-400' : 'border-slate-300'}`} data-testid="admin-password-input" autoFocus />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button type="submit" disabled={loading || !password.trim()} className={`w-full h-12 rounded-md font-semibold ${loading || !password.trim() ? 'bg-slate-200 text-slate-400' : 'bg-[#1B3A7B] text-white hover:bg-[#142d63]'}`} data-testid="admin-login-btn">{loading ? '...' : (language === 'pl' ? 'Zaloguj się' : 'Log in')}</button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-slate-500">{label}</span>
      </div>
      <div className="font-mono-price text-3xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function MiniBar({ data, colors, labels }) {
  const total = Object.values(data).reduce((s, v) => s + v, 0) || 1;
  return (
    <div className="space-y-2">
      {Object.entries(data).map(([key, val]) => (
        <div key={key} className="flex items-center gap-3">
          <span className="text-xs text-slate-600 w-20 truncate">{labels[key] || key}</span>
          <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${(val / total) * 100}%`, backgroundColor: colors[key] || '#94a3b8' }}
            />
          </div>
          <span className="font-mono-price text-xs font-bold text-slate-700 w-8 text-right">{val}</span>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ data, color = '#1B3A7B' }) {
  const entries = Object.entries(data).slice(-14);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="flex items-end gap-1 h-28">
      {entries.map(([date, val]) => (
        <div key={date} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] font-mono-price text-slate-500">{val}</span>
          <div
            className="w-full rounded-t transition-all duration-500"
            style={{ height: `${(val / max) * 80}%`, backgroundColor: color, minHeight: val > 0 ? 4 : 0 }}
          />
          <span className="text-[9px] text-slate-400">{date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { language } = useLanguage();
  const { isAdmin, token, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin && token) {
      axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setStats(res.data))
        .catch(() => toast.error('Failed to load stats'))
        .finally(() => setLoading(false));
    }
  }, [isAdmin, token]);

  if (authLoading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" /></div>;
  if (!isAdmin) return <AdminLoginGate />;

  const pl = language === 'pl';

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B]">
            {pl ? 'Dashboard' : 'Dashboard'}
          </h1>
          <p className="text-slate-500 mt-1">{pl ? 'Statystyki i analityka' : 'Statistics & analytics'}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/leads')} className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors" data-testid="dashboard-leads-btn">
            <Users className="w-4 h-4 inline mr-1.5" />{pl ? 'Leady' : 'Leads'}
          </button>
          <button onClick={() => navigate('/history')} className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors" data-testid="dashboard-history-btn">
            <FileText className="w-4 h-4 inline mr-1.5" />{pl ? 'Historia' : 'History'}
          </button>
          <button onClick={() => navigate('/')} className="px-4 py-2 rounded-md text-sm font-medium border-2 border-[#1B3A7B] text-[#1B3A7B] hover:bg-[#1B3A7B] hover:text-white transition-all flex items-center gap-1.5" data-testid="dashboard-back-btn">
            <ArrowLeft className="w-4 h-4" />{pl ? 'Kalkulator' : 'Calculator'}
          </button>
        </div>
      </div>

      {loading || !stats ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={FileText} label={pl ? 'Wyceny' : 'Quotes'} value={stats.total_quotes} color="#1B3A7B" />
            <StatCard icon={Users} label={pl ? 'Leady' : 'Leads'} value={stats.total_leads} color="#E8941A" />
            <StatCard icon={TrendingUp} label={pl ? 'Śr. wartość' : 'Avg value'} value={`${stats.avg_quote_value} zł`} color="#16a34a" />
            <StatCard icon={BarChart3} label={pl ? 'Konwersja' : 'Conversion'} value={stats.total_quotes > 0 ? `${Math.round((stats.total_leads / stats.total_quotes) * 100)}%` : '0%'} sub={pl ? 'leady / wyceny' : 'leads / quotes'} color="#8b5cf6" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quotes per day */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{pl ? 'Wyceny dziennie' : 'Quotes per day'}</h3>
              {Object.keys(stats.quotes_per_day).length > 0 ? (
                <DailyChart data={stats.quotes_per_day} color="#1B3A7B" />
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">{pl ? 'Brak danych' : 'No data'}</p>
              )}
            </div>

            {/* Leads per day */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{pl ? 'Leady dziennie' : 'Leads per day'}</h3>
              {Object.keys(stats.leads_per_day).length > 0 ? (
                <DailyChart data={stats.leads_per_day} color="#E8941A" />
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">{pl ? 'Brak danych' : 'No data'}</p>
              )}
            </div>

            {/* Popular forms */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{pl ? 'Popularne formy księgowości' : 'Popular accounting forms'}</h3>
              <MiniBar data={stats.form_counts} colors={BAR_COLORS} labels={FORM_LABELS} />
            </div>

            {/* Lead status */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">{pl ? 'Status leadów' : 'Lead status'}</h3>
              <MiniBar data={stats.leads_by_status} colors={STATUS_COLORS} labels={pl ? STATUS_LABELS_PL : { new: 'New', contacted: 'Contacted', converted: 'Converted', rejected: 'Rejected' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
